import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Loader2, ArrowLeft, GitBranch, PanelRightOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useWorkflowSession } from '@/hooks/useWorkflowSession';
import { WorkflowStream } from '@/components/workflow-builder-v2/WorkflowStream';
import { WorkflowCanvas } from '@/components/workflow-builder-v2/WorkflowCanvas';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { Sheet as MobileSheet, SheetContent as MobileSheetContent, SheetTrigger as MobileSheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { invokeWithTimeout } from '@/lib/invokeWithTimeout';
import type { ToolRecommendation } from '@/components/prompt-builder/ToolRecommendationCards';
import { deriveCarryForwardSources, type CarryForwardSource } from '@/lib/carryForward';
import {
  findStep,
  generateId,
  nextStep,
  nowIso,
  type AdjustPlanResponse,
  type AnalyzeOutputResponse,
  type ClarifyPayload,
  type DiscoveryResponse,
  type DraftStepPromptResponse,
  type StepState,
  type StreamMessage,
} from '@/lib/workflowStreamProtocol';

const PHASE_LABEL: Record<string, string> = {
  discovery: 'discovery',
  executing: 'building',
  finished: 'finished',
};

export default function WorkflowBuilderPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, membership } = useAuth();
  const isMobile = useIsMobile();

  // If this is /workflows/new (no id param), create a draft row and redirect.
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const bootstrapAttempted = useRef(false);
  // Derived: spinner while we're on /workflows/new and the insert is in flight.
  const bootstrapping = routeId === undefined && !bootstrapError;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const incomingInitialGoal: string | undefined = (location.state as any)?.initialGoal;

  useEffect(() => {
    if (routeId !== undefined || bootstrapAttempted.current || !user) return;
    bootstrapAttempted.current = true;

    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const insertRes: any = await (supabase as any)
          .from('workflow_conversations')
          .insert([{
            user_id: user.id,
            org_id: membership?.org_id ?? null,
            status: 'in_progress',
            phase: 'discovery',
            initial_goal: incomingInitialGoal ?? null,
          }])
          .select('id')
          .single();
        if (insertRes.error) throw insertRes.error;
        navigate(`/workflows/${insertRes.data.id}`, {
          replace: true,
          state: incomingInitialGoal ? { initialGoal: incomingInitialGoal } : undefined,
        });
      } catch (err) {
        console.error('[WorkflowBuilderPage] failed to create workflow:', err);
        setBootstrapError(err instanceof Error ? err.message : 'Failed to create workflow');
      }
    })();
  }, [routeId, user, membership, navigate, incomingInitialGoal]);

  const { session, isLoading, error, dispatch, flush } = useWorkflowSession(routeId ?? null);

  // Hard-pin the workflow builder to the viewport — no MainLayout wrapper so
  // there's no min-h-screen / overflow-auto chain that could let the grid
  // collapse smaller than the viewport (which is what made the page look like
  // it was "pulling away from the bottom"). The shell is the only thing in the
  // body; everything is bounded inside this fixed-inset container.
  return (
    <PinnedShell isMobile={isMobile}>
      {bootstrapping || isLoading || !session ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (error || bootstrapError) ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <p className="text-destructive text-sm">{error ?? bootstrapError}</p>
          <Button variant="outline" onClick={() => navigate('/workflows')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to workflows
          </Button>
        </div>
      ) : (
        <WorkflowBuilderShell
          sessionId={session.id}
          sessionName={session.name}
          sessionObjective={session.objective}
          sessionPhase={session.phase}
          activeStepId={session.activeStepId}
          selectedTool={session.selectedTool}
          steps={session.steps}
          messages={session.messages}
          onBack={async () => { await flush(); navigate('/workflows'); }}
          dispatch={dispatch}
        />
      )}
    </PinnedShell>
  );
}

/** Application shell — canonical Tailwind UI pattern.
 *
 * Desktop:
 *   - Sidebar is `fixed inset-y-0 left-0 w-64` — pinned to the viewport edges,
 *     out of flow. Body height is unaffected by sidebar content.
 *   - Main content sits behind a `pl-64` offset and is `h-screen overflow-hidden`,
 *     so it occupies exactly the viewport height. No body scroll.
 *
 * Mobile: full-screen flex column with a hamburger header + sheet for nav.
 */
function PinnedShell({ isMobile, children }: { isMobile: boolean; children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (isMobile) {
    return (
      <div className="h-screen flex flex-col bg-background overflow-hidden">
        <header className="h-14 grid grid-cols-3 items-center px-4 bg-background border-b border-border shrink-0">
          <div className="flex justify-start">
            <MobileSheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <MobileSheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11">
                  <Menu className="h-9 w-9" strokeWidth={2.5} />
                </Button>
              </MobileSheetTrigger>
              <MobileSheetContent side="left" className="p-0 w-64">
                <AppSidebar />
              </MobileSheetContent>
            </MobileSheet>
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed sidebar, out of flow. The arbitrary variant forces any nested
          aside to position:static — AppSidebar uses `sticky top-0` for the
          flex-row layout in MainLayout, but inside this fixed wrapper sticky
          has no scroll container and can behave inconsistently. */}
      <div className="fixed inset-y-0 left-0 w-64 z-40 [&_aside]:!static [&_aside]:!h-full">
        <AppSidebar />
      </div>
      {/* Main content sized to the viewport, offset by the sidebar. */}
      <main className="pl-64 h-screen overflow-hidden bg-background">
        <div className="h-full w-full overflow-hidden">
          {children}
        </div>
      </main>
    </>
  );
}

// ── Shell (separated so it doesn't re-create handlers on hydrate) ────────

interface WorkflowBuilderShellProps {
  sessionId: string;
  sessionName: string | null;
  sessionObjective: string | null;
  sessionPhase: 'discovery' | 'executing' | 'finished';
  activeStepId: string | null;
  selectedTool: string | null;
  steps: StepState[];
  messages: StreamMessage[];
  onBack: () => void;
  dispatch: ReturnType<typeof useWorkflowSession>['dispatch'];
}

function WorkflowBuilderShell({
  sessionId,
  sessionName,
  sessionObjective,
  sessionPhase,
  activeStepId,
  selectedTool,
  steps,
  messages,
  onBack,
  dispatch,
}: WorkflowBuilderShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [compose, setCompose] = useState('');
  // Ref counter instead of a boolean — multiple async operations (drafter,
  // discovery, analysis, adjust-plan) can overlap. isTyping is true when any
  // are in flight, and only goes false when all have finished.
  const inflightRef = useRef(0);
  const [isTyping, setIsTyping] = useState(false);
  const [pendingClarifyMessageIds, setPendingClarifyMessageIds] = useState<Set<string>>(new Set());
  const [awaitingPlanApproval, setAwaitingPlanApproval] = useState(false);
  /** Step IDs currently waiting on a remix call (UI: spinner on artifact). */
  const [remixingStepIds, setRemixingStepIds] = useState<Set<string>>(new Set());
  /** Workflow-level tool recommendations (fetched after proposal). */
  const [toolRecommendations, setToolRecommendations] = useState<ToolRecommendation[] | null>(null);
  const [isLoadingToolRecs, setIsLoadingToolRecs] = useState(false);
  const [isOptimizingForTool, setIsOptimizingForTool] = useState(false);
  const draftingTriggered = useRef<Set<string>>(new Set());
  const scoringInFlight = useRef<Set<string>>(new Set());
  const absorbedDraftRef = useRef(false);
  const initialGoalAutoSentRef = useRef(false);
  // Hydration draft fires AT MOST ONCE per page load — never in response to
  // user-initiated active-step changes (canvas/rail clicks).
  const hydrationDraftFiredRef = useRef(false);

  // ── Build the messages array we send to the discovery edge fn ──────
  const toLLMHistory = useCallback((msgs: StreamMessage[]) => {
    return msgs
      .filter(m => m.kind === 'chat' || m.kind === 'proposal' || m.kind === 'clarify')
      .map(m => {
        if (m.kind === 'chat') return { role: m.role, content: m.content };
        if (m.kind === 'proposal') return { role: 'assistant' as const, content: JSON.stringify({ type: 'proposal', proposal: m.payload }) };
        return { role: 'assistant' as const, content: JSON.stringify({ type: 'clarify', clarify: m.payload }) };
      });
  }, []);

  // ── Handle a discovery response from the edge fn ────────────────────
  const handleDiscoveryResponse = useCallback((resp: DiscoveryResponse, ts: string) => {
    if (resp.type === 'task_redirect') {
      dispatch({
        type: 'append_message',
        message: { kind: 'chat', role: 'assistant', content: resp.message, id: generateId(), ts },
      });
      dispatch({
        type: 'append_message',
        message: { kind: 'task_redirect', id: generateId(), ts },
      });
      return;
    }

    if (resp.type === 'clarify') {
      dispatch({
        type: 'append_message',
        message: { kind: 'clarify', payload: resp.clarify, id: generateId(), ts },
      });
      return;
    }

    if (resp.type === 'proposal') {
      const proposal = resp.proposal;
      // Dedup: when a refined proposal arrives, prune the prior proposal and
      // any stale unanswered clarify messages from the stream so the UI doesn't
      // duplicate them. We keep user chat answers (those carry context).
      const stalePriorIds = messages
        .filter(m => m.kind === 'proposal' || m.kind === 'clarify')
        .map(m => m.id);
      if (stalePriorIds.length > 0) {
        dispatch({ type: 'remove_messages', ids: stalePriorIds });
      }

      // Append the proposal narration
      dispatch({
        type: 'append_message',
        message: { kind: 'proposal', payload: proposal, id: generateId(), ts },
      });
      if (resp.chat) {
        dispatch({
          type: 'append_message',
          message: { kind: 'chat', role: 'assistant', content: resp.chat, id: generateId(), ts },
        });
      }
      // Append any clarifying questions as separate clarify messages.
      const hasClarifyingQs = proposal.clarifyingQuestions && proposal.clarifyingQuestions.length > 0;
      if (hasClarifyingQs) {
        proposal.clarifyingQuestions!.forEach((q: ClarifyPayload) => {
          dispatch({
            type: 'append_message',
            message: { kind: 'clarify', payload: q, id: generateId(), ts: nowIso() },
          });
        });
      }

      // Only seed steps when the proposal has no outstanding clarifying
      // questions. When clarifies exist the user's answers will trigger a
      // refined proposal — seeding now would create steps that get clobbered
      // by the refined seed (the position unique constraint causes the second
      // upsert batch to 409, while the delete of the first batch succeeds,
      // leaving zero steps).
      if (!hasClarifyingQs) {
        dispatch({ type: 'seed_from_proposal', proposal });
      }

      setAwaitingPlanApproval(!hasClarifyingQs);

      // Fetch workflow-level tool recommendations once we have a finalized proposal.
      if (!hasClarifyingQs) {
        void fetchToolRecommendations(proposal.objective);
      }
    }
  }, [dispatch, messages, navigate, sessionObjective]);

  const fetchToolRecommendations = useCallback(async (objective: string) => {
    setIsLoadingToolRecs(true);
    try {
      const { data, error } = await invokeWithTimeout<{ recommendations: ToolRecommendation[] }>(
        'recommend-tools-inline-v6',
        { final_prompt: objective },
        30_000,
      );
      if (!error && data?.recommendations) {
        setToolRecommendations(data.recommendations);
      }
    } finally {
      setIsLoadingToolRecs(false);
    }
  }, []);

  // ── Restore tool selector state on page re-hydration ───────────────
  // When the user navigates away and back, React state is lost. If the
  // workflow is in discovery with seeded steps but no tool selected yet,
  // re-show the plan approval gate and re-fetch tool recommendations.
  const toolRehydratedRef = useRef(false);
  useEffect(() => {
    if (toolRehydratedRef.current) return;
    if (
      sessionPhase === 'discovery' &&
      steps.length > 0 &&
      !selectedTool &&
      sessionObjective
    ) {
      toolRehydratedRef.current = true;
      setAwaitingPlanApproval(true);
      void fetchToolRecommendations(sessionObjective);
    }
  }, [sessionPhase, steps.length, selectedTool, sessionObjective, fetchToolRecommendations]);

  // ── Send to the discovery edge fn ───────────────────────────────────
  const sendToDiscovery = useCallback(async (afterDispatchMessages: StreamMessage[]) => {
    inflightRef.current++; setIsTyping(true);
    try {
      const llmHistory = toLLMHistory(afterDispatchMessages);
      const { data, error: classified } = await invokeWithTimeout<DiscoveryResponse>(
        'workflow-builder-discovery',
        { messages: llmHistory },
        60_000,
      );
      if (classified) {
        dispatch({
          type: 'append_message',
          message: { kind: 'system', text: classified.message, id: generateId(), ts: nowIso() },
        });
        return;
      }
      if (!data) {
        dispatch({
          type: 'append_message',
          message: { kind: 'system', text: 'Cosmo had trouble responding. Please try again.', id: generateId(), ts: nowIso() },
        });
        return;
      }
      handleDiscoveryResponse(data, nowIso());
    } finally {
      inflightRef.current--; setIsTyping(inflightRef.current > 0);
    }
  }, [toLLMHistory, handleDiscoveryResponse, dispatch]);

  // ── Compose submit (user free-form message during discovery) ────────
  const handleSubmit = useCallback(() => {
    const text = compose.trim();
    if (!text || isTyping) return;
    setCompose('');

    // User is refining; clear any pending approval CTA.
    setAwaitingPlanApproval(false);

    const userMsg: StreamMessage = {
      kind: 'chat', role: 'user', content: text, id: generateId(), ts: nowIso(),
    };
    dispatch({ type: 'append_message', message: userMsg });

    // If this is the first user message and objective is null, also set it.
    if (!sessionObjective) {
      dispatch({ type: 'set_meta', objective: text });
    }

    void sendToDiscovery([...messages, userMsg]);
  }, [compose, isTyping, dispatch, messages, sendToDiscovery, sessionObjective]);

  // ── Clarifying-question answer (chip click or freeform) ─────────────
  // We collect answers and only send to discovery once every clarify in the
  // current batch has been answered. This prevents the model from re-asking
  // a still-pending question and duplicating it in the stream.
  //
  // Uses a ref so rapid chip clicks (batched by React) always see the
  // latest answered set instead of a stale state snapshot.
  const answeredRef = useRef<Set<string>>(new Set());
  const handleClarifyAnswer = useCallback((messageId: string, answer: string) => {
    if (answeredRef.current.has(messageId)) return;
    answeredRef.current = new Set(answeredRef.current).add(messageId);
    setPendingClarifyMessageIds(answeredRef.current);
    setAwaitingPlanApproval(false);

    const userMsg: StreamMessage = {
      kind: 'chat', role: 'user', content: answer, id: generateId(), ts: nowIso(),
    };
    dispatch({ type: 'append_message', message: userMsg });

    // Find every clarify message in the current stream — that's the batch we
    // need fully answered before we send.
    const clarifyIds = messages.filter(m => m.kind === 'clarify').map(m => m.id);
    const allAnswered = clarifyIds.every(id => answeredRef.current.has(id));
    if (!allAnswered) {
      // Still more to answer — hold the send.
      return;
    }

    // Send the consolidated history to discovery.
    void sendToDiscovery([...messages, userMsg]);
    // Reset for the next round of clarifying questions.
    answeredRef.current = new Set();
    setPendingClarifyMessageIds(new Set());
  }, [dispatch, messages, sendToDiscovery]);

  // ── Per-step prompt drafting ───────────────────────────────────────

  const callDrafter = useCallback(async (stepId: string, tweakRequest?: string) => {
    const step = findStep(steps, stepId);
    if (!step) return;
    inflightRef.current++; setIsTyping(true);
    // Clear any prior error before retrying.
    dispatch({ type: 'set_draft_error', stepId, error: null });
    if (step.phase === 'proposed' || step.phase === 'prompt_drafting') {
      dispatch({ type: 'set_step_phase', stepId, phase: 'prompt_drafting' });
    }
    try {
      const { data, error: classified } = await invokeWithTimeout<DraftStepPromptResponse>(
        'workflow-builder-draft-step-prompt',
        { workflow_id: sessionId, step_id: stepId, tweak_request: tweakRequest },
        60_000,
      );
      if (classified) {
        // Flip back to proposed so the user isn't stuck in prompt_drafting,
        // then surface the error inline.
        dispatch({ type: 'set_step_phase', stepId, phase: 'proposed' });
        dispatch({
          type: 'set_draft_error',
          stepId,
          error: { kind: classified.kind, message: classified.message, retryable: classified.retryable },
        });
        // Allow drafting to be re-triggered later.
        draftingTriggered.current.delete(stepId);
        return;
      }
      if (!data) {
        dispatch({ type: 'set_step_phase', stepId, phase: 'proposed' });
        dispatch({
          type: 'set_draft_error',
          stepId,
          error: { kind: 'unknown', message: 'Cosmo returned an empty response.', retryable: true },
        });
        draftingTriggered.current.delete(stepId);
        return;
      }
      const resp = data;
      const ts = nowIso();

      if (resp.type === 'clarify') {
        dispatch({
          type: 'append_message',
          message: { kind: 'clarify', payload: resp.clarify, stepId, id: generateId(), ts },
        });
        return;
      }

      if (resp.type === 'prompt_draft') {
        const nextVersion = (step.promptDraft?.version ?? 0) + 1;
        const draft = { ...resp.draft, version: nextVersion };
        dispatch({ type: 'set_prompt_draft', stepId, draft });
        dispatch({
          type: 'append_message',
          message: { kind: 'prompt_artifact', stepId, payload: draft, id: generateId(), ts },
        });

        // Score the prompt in the background so it counts as a "prompt created"
        // and appears in the user's Vault. Skip if already scored, in-flight, or too short.
        if (!step.scoreId && !scoringInFlight.current.has(stepId) && draft.body && draft.body.trim().length >= 30) {
          scoringInFlight.current.add(stepId);
          void (async () => {
            try {
              const { data: scoreData, error: scoreErr } = await supabase.functions.invoke('score-submission', {
                body: {
                  content: draft.body,
                  submission_type: 'prompt_single',
                  source: 'workflow',
                  is_public: false,
                },
              });
              if (!scoreErr && scoreData?.score_id && scoreData?.submission_id) {
                dispatch({
                  type: 'set_step_score',
                  stepId,
                  scoreId: scoreData.score_id,
                  submissionId: scoreData.submission_id,
                });
                queryClient.invalidateQueries({ queryKey: ['library-recent'] });
                queryClient.invalidateQueries({ queryKey: ['library-counts'] });
              }
            } catch (e) {
              console.warn('[WorkflowBuilder] Background scoring failed:', e);
            } finally {
              scoringInFlight.current.delete(stepId);
            }
          })();
        }
      }
    } finally {
      inflightRef.current--; setIsTyping(inflightRef.current > 0);
    }
  }, [steps, dispatch, sessionId, queryClient]);

  // Explicit drafter trigger — called only from "Start building" and "Proceed",
  // never from canvas clicks. Prevents stray prompts when the user is just
  // inspecting future steps.
  const triggerDraftForStep = useCallback((stepId: string) => {
    if (draftingTriggered.current.has(stepId)) return;
    const step = findStep(steps, stepId);
    if (!step || step.phase !== 'proposed') return;
    draftingTriggered.current.add(stepId);
    void callDrafter(stepId);
  }, [steps, callDrafter]);

  // Retry the drafter for the active step (called from DraftErrorCard).
  const handleRetryDraft = useCallback((stepId: string) => {
    draftingTriggered.current.delete(stepId);
    triggerDraftForStep(stepId);
  }, [triggerDraftForStep]);


  // On hydration (reload mid-execution), trigger drafting for the active step
  // exactly once — but only if all prior steps are done. If the user navigated
  // to a future step and refreshed, we shouldn't auto-draft it.
  useEffect(() => {
    if (hydrationDraftFiredRef.current) return;
    if (sessionPhase !== 'executing' || !activeStepId) return;
    hydrationDraftFiredRef.current = true;
    const active = findStep(steps, activeStepId);
    if (!active) return;
    const priorDone = steps.filter(s => s.position < active.position).every(s => s.phase === 'done');
    if (priorDone) {
      triggerDraftForStep(activeStepId);
    }
  }, [sessionPhase, activeStepId, steps, triggerDraftForStep]);

  // Auto-send an incoming initial goal as the first user message (CosmoHome handoff).
  useEffect(() => {
    if (initialGoalAutoSentRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initialGoal: string | undefined = (location.state as any)?.initialGoal;
    if (!initialGoal || !initialGoal.trim()) return;
    if (messages.length > 0) {
      initialGoalAutoSentRef.current = true;
      return;
    }
    initialGoalAutoSentRef.current = true;
    const userMsg: StreamMessage = {
      kind: 'chat', role: 'user', content: initialGoal.trim(), id: generateId(), ts: nowIso(),
    };
    dispatch({ type: 'append_message', message: userMsg });
    if (!sessionObjective) dispatch({ type: 'set_meta', objective: initialGoal.trim() });
    void sendToDiscovery([userMsg]);
    // Clear the state so a reload doesn't re-send it.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, messages.length, dispatch, sessionObjective, sendToDiscovery, navigate]);

  // Absorb a returning prompt from the Prompt Builder escape hatch.
  useEffect(() => {
    if (absorbedDraftRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const draft = (location.state as any)?.workflowDraft;
    if (!draft || !draft.stepId || !draft.promptBody) return;
    const step = findStep(steps, draft.stepId);
    if (!step) return;
    absorbedDraftRef.current = true;
    const version = (step.promptDraft?.version ?? 0) + 1;
    const payload = {
      version,
      title: step.stepName,
      body: draft.promptBody,
      tool: step.recommendedTool ?? 'AI tool',
    };
    dispatch({ type: 'set_prompt_draft', stepId: draft.stepId, draft: payload });
    dispatch({
      type: 'append_message',
      message: { kind: 'prompt_artifact', stepId: draft.stepId, payload, id: generateId(), ts: nowIso() },
    });
    // Clear the location state so reload doesn't re-apply.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, steps, dispatch, navigate]);

  // ── Output paste + analysis ────────────────────────────────────────

  const handleAnalyzeOutput = useCallback(async (stepId: string, text: string, fileMeta?: { filename?: string; mimeType?: string }) => {
    const step = findStep(steps, stepId);
    if (!step) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    const bytes = new Blob([trimmed]).size;
    const rows = trimmed.split(/\n/).filter(Boolean).length;
    const mimeHint: 'csv' | 'json' | 'markdown' | 'plain' = fileMeta?.mimeType?.includes('csv') ? 'csv'
      : fileMeta?.mimeType?.includes('json') ? 'json'
      : fileMeta?.mimeType?.includes('markdown') ? 'markdown'
      : /^[^\s,]+(,[^\s,]+)+/.test(trimmed) ? 'csv'
      : /^\s*[[{]/.test(trimmed) ? 'json'
      : /^#|^\*/.test(trimmed) ? 'markdown'
      : 'plain';
    const output = {
      text: trimmed,
      meta: {
        bytes,
        rows,
        mimeHint,
        ...(fileMeta?.filename ? { filename: fileMeta.filename } : {}),
      } as const,
    };

    dispatch({ type: 'set_pasted_output', stepId, output });
    dispatch({
      type: 'append_message',
      message: { kind: 'pasted_output', stepId, payload: output, id: generateId(), ts: nowIso() },
    });

    inflightRef.current++; setIsTyping(true);
    try {
      const { data, error: classified } = await invokeWithTimeout<AnalyzeOutputResponse>(
        'workflow-builder-analyze-output',
        { workflow_id: sessionId, step_id: stepId },
        90_000,
      );
      if (classified || !data || data.type !== 'analysis') {
        // Reset phase so the user can re-paste or retry instead of being
        // stuck on "Analyzing output…" forever.
        dispatch({ type: 'set_step_phase', stepId, phase: 'prompt_ready' });
        dispatch({
          type: 'append_message',
          message: { kind: 'system', text: classified?.message ?? 'Cosmo had trouble analyzing the output. Try again.', stepId, id: generateId(), ts: nowIso() },
        });
        return;
      }
      dispatch({ type: 'set_analysis', stepId, analysis: data.analysis });
      dispatch({
        type: 'append_message',
        message: { kind: 'analysis', stepId, payload: data.analysis, id: generateId(), ts: nowIso() },
      });
    } finally {
      inflightRef.current--; setIsTyping(inflightRef.current > 0);
    }
  }, [steps, dispatch, sessionId]);

  const handleProceedFromStep = useCallback((stepId: string) => {
    const upcoming = nextStep(steps, stepId);
    if (upcoming) {
      dispatch({ type: 'set_active_step', stepId: upcoming.id });
      // Tag the transition with the NEW step's id so it appears as the
      // opening message of the next step's view (not the previous one's).
      dispatch({
        type: 'append_message',
        message: { kind: 'system', text: `Proceeding to step ${upcoming.position + 1}: ${upcoming.stepName}`, stepId: upcoming.id, id: generateId(), ts: nowIso() },
      });
      triggerDraftForStep(upcoming.id);
    } else {
      const finalStep = findStep(steps, stepId);
      dispatch({ type: 'mark_finished' });
      dispatch({
        type: 'append_message',
        message: { kind: 'system', text: 'Workflow complete.', stepId: finalStep?.id, id: generateId(), ts: nowIso() },
      });
    }
  }, [steps, dispatch, triggerDraftForStep]);

  const handleStartBuilding = useCallback(() => {
    if (steps.length === 0) return;
    setAwaitingPlanApproval(false);
    const first = steps[0];
    dispatch({ type: 'set_workflow_phase', phase: 'executing' });
    dispatch({ type: 'set_active_step', stepId: first.id });
    // Tag with the step id so it appears in step 1's view (not orphaned).
    dispatch({
      type: 'append_message',
      message: { kind: 'system', text: `Starting step ${first.position + 1}: ${first.stepName}`, stepId: first.id, id: generateId(), ts: nowIso() },
    });
    triggerDraftForStep(first.id);
  }, [steps, dispatch, triggerDraftForStep]);

  const handleIterateStep = useCallback((stepId: string) => {
    // Reset to prompt_ready so the user can paste different output or re-run.
    dispatch({ type: 'set_step_phase', stepId, phase: 'prompt_ready' });
  }, [dispatch]);

  // Record a user's choice for one of the analysis decisions, then ask the
  // drafter for a follow-up prompt that bakes in the direction. The user can
  // copy that into the SAME tool conversation to course-correct, OR proceed.
  const handleRecordDecision = useCallback(async (stepId: string, decision: { question: string; chosen: string }) => {
    dispatch({ type: 'record_decision', stepId, decision });
    // Also surface the user's pick in the step's chat as a system breadcrumb.
    dispatch({
      type: 'append_message',
      message: {
        kind: 'system',
        text: `You chose: ${decision.chosen}`,
        stepId,
        id: generateId(),
        ts: nowIso(),
      },
    });
    // Generate a follow-up prompt for the same external tool that steers it
    // toward the chosen direction. Uses the existing remix path so the new
    // version lands as a fresh prompt_artifact in this step's stream.
    const tweak = `The user reviewed the previous output and chose: "${decision.chosen}" (decision: "${decision.question}"). Produce a follow-up prompt the user can paste into the SAME external tool conversation to refine the previous output in that direction. Keep it short, reference the previous output, and end with an explicit "return only the {format}" line.`;
    setRemixingStepIds(prev => new Set(prev).add(stepId));
    try {
      await callDrafter(stepId, tweak);
    } finally {
      setRemixingStepIds(prev => {
        const next = new Set(prev);
        next.delete(stepId);
        return next;
      });
    }
  }, [dispatch, callDrafter]);

  // (handleInlineCarryForward + carryForwardByStep live below
  //  handleSavePromptEdit so the closure can capture it without TDZ.)

  // ── Dynamic plan adjustment ────────────────────────────────────────

  const sendToAdjustPlan = useCallback(async (userMessage: string, contextStepId?: string) => {
    inflightRef.current++; setIsTyping(true);
    try {
      const { data, error: classified } = await invokeWithTimeout<AdjustPlanResponse>(
        'workflow-builder-adjust-plan',
        { workflow_id: sessionId, user_message: userMessage },
        30_000,
      );
      if (classified) {
        dispatch({
          type: 'append_message',
          message: { kind: 'system', text: classified.message, stepId: contextStepId, id: generateId(), ts: nowIso() },
        });
        return;
      }
      if (!data) {
        dispatch({
          type: 'append_message',
          message: { kind: 'system', text: 'Cosmo had trouble adjusting the plan. Try again.', stepId: contextStepId, id: generateId(), ts: nowIso() },
        });
        return;
      }
      const resp = data;
      const ts = nowIso();
      if (resp.type === 'chat') {
        dispatch({
          type: 'append_message',
          message: { kind: 'chat', role: 'assistant', content: resp.content, stepId: contextStepId, id: generateId(), ts },
        });
        return;
      }
      if (resp.type === 'plan_diff') {
        if (resp.diff.ops && resp.diff.ops.length > 0) {
          dispatch({ type: 'apply_plan_diff', ops: resp.diff.ops });
        }
        if (resp.diff.chat) {
          dispatch({
            type: 'append_message',
            message: { kind: 'chat', role: 'assistant', content: resp.diff.chat, stepId: contextStepId, id: generateId(), ts },
          });
        }
      }
    } finally {
      inflightRef.current--; setIsTyping(inflightRef.current > 0);
    }
  }, [dispatch, sessionId]);

  // ── Workflow-level tool change: re-optimize all existing drafts ─────
  const handleChangeWorkflowTool = useCallback(async (rec: ToolRecommendation) => {
    const newTool = rec.tool;
    if (newTool === selectedTool) return;

    // 1. Update workflow-level tool + propagate to all steps
    dispatch({ type: 'set_selected_tool', tool: newTool });

    // 2. Re-optimize all steps that already have prompt drafts
    const stepsWithDrafts = steps.filter(s => s.promptDraft);
    if (stepsWithDrafts.length === 0) return;

    setIsOptimizingForTool(true);
    try {
      const results = await Promise.all(
        stepsWithDrafts.map(async (step) => {
          const { data, error } = await invokeWithTimeout<{ optimized_prompt: string }>(
            'optimize-prompt-for-tool-v6',
            {
              final_prompt: step.promptDraft!.body,
              selected_tool: newTool,
              selected_model: rec.model || '',
            },
            60_000,
          );
          return { stepId: step.id, data, error, originalDraft: step.promptDraft! };
        }),
      );

      for (const result of results) {
        if (!result.error && result.data?.optimized_prompt) {
          const currentStep = findStep(steps, result.stepId);
          const nextVersion = (currentStep?.promptDraft?.version ?? result.originalDraft.version) + 1;
          const newDraft = {
            ...result.originalDraft,
            version: nextVersion,
            body: result.data.optimized_prompt,
            tool: newTool,
          };
          dispatch({ type: 'set_prompt_draft', stepId: result.stepId, draft: newDraft });
          dispatch({
            type: 'append_message',
            message: {
              kind: 'prompt_artifact' as const,
              stepId: result.stepId,
              payload: newDraft,
              id: generateId(),
              ts: nowIso(),
            },
          });
        }
      }
    } finally {
      setIsOptimizingForTool(false);
    }
  }, [steps, dispatch, selectedTool]);

  // ── Remix: user adds context, drafter rebuilds ──────────────────────
  const handleRemixPrompt = useCallback(async (stepId: string, additionalContext: string) => {
    setRemixingStepIds(prev => new Set(prev).add(stepId));
    try {
      await callDrafter(stepId, additionalContext);
    } finally {
      setRemixingStepIds(prev => {
        const next = new Set(prev);
        next.delete(stepId);
        return next;
      });
    }
  }, [callDrafter]);

  // ── Inline edit on prompt artifacts ────────────────────────────────

  const handleSavePromptEdit = useCallback((stepId: string, newBody: string) => {
    const step = findStep(steps, stepId);
    if (!step) return;
    const current = step.promptDraft;
    const draft = {
      version: (current?.version ?? 0) + 1,
      title: current?.title ?? step.stepName,
      body: newBody,
      tool: current?.tool ?? step.recommendedTool ?? 'AI tool',
      toolNotes: current?.toolNotes,
    };
    dispatch({ type: 'set_prompt_draft', stepId, draft });
    dispatch({
      type: 'append_message',
      message: { kind: 'prompt_artifact', stepId, payload: draft, id: generateId(), ts: nowIso() },
    });
  }, [steps, dispatch]);

  // Inline a prior step's output into the active step's prompt body.
  // Declared after handleSavePromptEdit so the closure captures it cleanly.
  const handleInlineCarryForward = useCallback((stepId: string, newBody: string) => {
    handleSavePromptEdit(stepId, newBody);
  }, [handleSavePromptEdit]);

  // For each step that has a drafted prompt, the carry-forward sources are
  // every prior step's pasted output.
  const carryForwardByStep = useMemo(() => {
    const m = new Map<string, CarryForwardSource[]>();
    for (const step of steps) {
      if (!step.promptDraft) continue;
      const prior = steps.filter(s => s.position < step.position);
      const sources = deriveCarryForwardSources(prior);
      if (sources.length > 0) m.set(step.id, sources);
    }
    return m;
  }, [steps]);

  const handleOpenInPromptBuilder = useCallback((stepId: string) => {
    const step = findStep(steps, stepId);
    if (!step) return;
    navigate('/build/new', {
      state: {
        initialGoal: step.promptDraft?.body ?? step.description ?? step.stepName,
        workflowReturn: { workflowId: sessionId, stepId },
        returnTo: `/workflows/${sessionId}`,
      },
    });
  }, [steps, navigate, sessionId]);

  // ── Compose box submit routes based on workflow phase ──────────────
  const baseHandleSubmit = handleSubmit;
  const submit = useCallback(() => {
    const text = compose.trim();
    if (!text || isTyping) return;

    // During discovery, free-form goes to the discovery edge fn.
    if (sessionPhase === 'discovery') {
      baseHandleSubmit();
      return;
    }

    // During executing/finished, free-form goes to the plan-adjuster. Tag
    // both the user's message and the assistant response with the active
    // step id so the conversation stays in that step's view.
    setCompose('');
    const userMsg: StreamMessage = {
      kind: 'chat', role: 'user', content: text, stepId: activeStepId ?? undefined, id: generateId(), ts: nowIso(),
    };
    dispatch({ type: 'append_message', message: userMsg });
    void sendToAdjustPlan(text, activeStepId ?? undefined);
  }, [compose, isTyping, sessionPhase, activeStepId, dispatch, baseHandleSubmit, sendToAdjustPlan]);

  // Belt-and-suspenders viewport lock. The PinnedShell already pins everything
  // to the viewport, but some browsers (notably Chromium with HMR) can have
  // residual body scroll position from prior pages or transient layout shifts
  // during HMR. Force html + body to 100% height with overflow:hidden +
  // overscroll-behavior:none. Reset scrollY on mount.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
    };
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    html.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    body.style.overscrollBehavior = 'none';
    window.scrollTo(0, 0);
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      body.style.overscrollBehavior = prev.bodyOverscroll;
    };
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_460px] h-full min-h-0 overflow-hidden">
      {/* Center stream */}
      <div className="flex flex-col min-h-0 overflow-hidden">
        <header className="h-[52px] border-b border-border flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center gap-3 text-sm min-w-0">
            <Button variant="ghost" size="icon" onClick={onBack} className="h-7 w-7 shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <GitBranch className="h-4 w-4 text-primary shrink-0" />
            <span className="text-muted-foreground hidden sm:inline">Workflows</span>
            <span className="opacity-40 hidden sm:inline">/</span>
            <span className="font-medium truncate">
              {sessionName ?? 'New workflow'}
            </span>
            <Badge variant="outline" className="text-[10px] px-2 py-0.5 shrink-0">
              {PHASE_LABEL[sessionPhase] ?? sessionPhase}
            </Badge>
          </div>
          {/* Mobile canvas trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="lg:hidden gap-1.5 text-xs">
                <PanelRightOpen className="h-4 w-4" />
                {steps.length > 0 ? `${steps.filter(s => s.phase === 'done').length}/${steps.length}` : 'canvas'}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[90vw] sm:w-[420px] p-0">
              <WorkflowCanvas
                steps={steps}
                activeStepId={activeStepId}
                workflowPhase={sessionPhase}
                onSelectStep={(id) => dispatch({ type: 'set_active_step', stepId: id })}
              />
            </SheetContent>
          </Sheet>
        </header>

        <WorkflowStream
          messages={messages}
          workflowPhase={sessionPhase}
          isTyping={isTyping}
          composeValue={compose}
          onComposeChange={setCompose}
          onSubmit={submit}
          onClarifyAnswer={handleClarifyAnswer}
          onSavePromptEdit={handleSavePromptEdit}
          onRemixPrompt={handleRemixPrompt}
          remixingStepIds={remixingStepIds}
          onRecordDecision={handleRecordDecision}
          onInlineCarryForward={handleInlineCarryForward}
          carryForwardByStep={carryForwardByStep}
          workflowName={sessionName}
          onOpenInPromptBuilder={handleOpenInPromptBuilder}
          activeStep={activeStepId ? findStep(steps, activeStepId) ?? null : null}
          workflowId={sessionId}
          nextStepLabel={(() => {
            if (!activeStepId) return undefined;
            const upcoming = nextStep(steps, activeStepId);
            return upcoming ? upcoming.stepName : undefined;
          })()}
          onAnalyzeOutput={handleAnalyzeOutput}
          onProceedFromStep={handleProceedFromStep}
          onIterateStep={handleIterateStep}
          awaitingPlanApproval={awaitingPlanApproval}
          planStepCount={steps.length}
          planName={sessionName}
          onStartBuilding={handleStartBuilding}
          toolRecommendations={toolRecommendations}
          isLoadingToolRecs={isLoadingToolRecs}
          isOptimizingForTool={isOptimizingForTool}
          selectedTool={selectedTool}
          onSelectWorkflowTool={handleChangeWorkflowTool}
          onRetryDraft={handleRetryDraft}
          composePlaceholder={
            messages.length === 0
              ? 'What are you trying to accomplish? Describe the objective…'
              : sessionPhase === 'discovery'
                ? 'Answer in your own words, or pick an option above…'
                : 'Ask Cosmo to adjust the workflow, swap tools, or add context…'
          }
          disabled={isTyping}
          onGoToPromptBuilder={() => {
            navigate('/build/new', {
              state: {
                initialGoal: sessionObjective ?? messages.find(m => 'role' in m && m.role === 'user')?.content,
                returnTo: '/workflows',
              },
            });
          }}
          onTriggerDraft={(stepId) => triggerDraftForStep(stepId)}
          priorStepsDone={(() => {
            if (!activeStepId) return false;
            const active = findStep(steps, activeStepId);
            if (!active) return false;
            return steps.filter(s => s.position < active.position).every(s => s.phase === 'done');
          })()}
        />
      </div>

      {/* Right canvas (desktop) */}
      <div className="hidden lg:flex min-h-0 flex-col overflow-hidden">
        <WorkflowCanvas
          steps={steps}
          activeStepId={activeStepId}
          workflowPhase={sessionPhase}
          onSelectStep={(id) => dispatch({ type: 'set_active_step', stepId: id })}
        />
      </div>

      {/* Persist session id reference to avoid unused warning */}
      <div hidden>{sessionId}</div>
    </div>
  );
}
